import { useState } from 'react';

import { isAxiosError } from 'axios';

import {
  countersApi,
  type CounterRow,
  type CounterTable,
} from '@/api/queries/counters';
import { Button, Input, Modal, Textarea, useToast } from '@/components/ui';

interface Props {
  table: CounterTable;
  activeLang: string;
  onClose: () => void;
}

export function AddCounterRowModal({ table, activeLang, onClose }: Props) {
  const add = countersApi.useAddRow();
  const toast = useToast();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [parent, setParent] = useState('');
  const [custom, setCustom] = useState<Record<string, unknown>>({});

  const submit = async () => {
    // The row key is required and must be non-blank. A blank key would create an
    // empty-keyed row the first time and then collide (409) on every retry, so we
    // stop it here rather than letting the server reject a malformed request.
    const trimmedKey = key.trim();
    if (!trimmedKey) {
      toast.show({ severity: 'error', title: 'Key is required' });
      return;
    }
    const row: CounterRow = {
      key: trimmedKey,
      value: { [activeLang]: value },
      description: description ? { [activeLang]: description } : undefined,
      parent: parent || undefined,
      custom,
    };
    try {
      await add.mutateAsync({ key: table.key, row });
      toast.show({ severity: 'success', title: `Added row ${trimmedKey}` });
      onClose();
    } catch (err) {
      // Surface the server's reason (e.g. "row 'PRG' already exists") instead of
      // the generic Axios "Request failed with status code 409".
      const conflict = isAxiosError(err) && err.response?.status === 409;
      const serverError =
        isAxiosError(err) && typeof err.response?.data?.error === 'string'
          ? (err.response.data.error as string)
          : undefined;
      toast.show({
        severity: 'error',
        title: conflict
          ? `A row with key "${trimmedKey}" already exists`
          : serverError ?? (err instanceof Error ? err.message : 'Failed to add row'),
      });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Add row"
      subtitle={`Adding values for ${activeLang}. Other languages can be added inline after creation, or auto-translated.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={add.isPending} onClick={submit}>
            Add row
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 10 }}>
        <Field label="Key">
          <Input
            value={key}
            placeholder="PRG"
            onChange={(e) => setKey(e.currentTarget.value)}
          />
        </Field>
        <Field label={`Value (${activeLang})`}>
          <Input value={value} onChange={(e) => setValue(e.currentTarget.value)} />
        </Field>
        <Field label={`Description (${activeLang})`}>
          <Textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
        </Field>
        <Field label="Parent (row key)">
          <Input
            value={parent}
            placeholder="CZ"
            onChange={(e) => setParent(e.currentTarget.value)}
          />
        </Field>
        {(table.customFields ?? []).map((cf) => (
          <Field key={cf.key} label={`${cf.label}${cf.required ? ' *' : ''}`}>
            {cf.type === 'boolean' ? (
              <input
                type="checkbox"
                checked={Boolean(custom[cf.key])}
                onChange={(e) =>
                  setCustom((prev) => ({ ...prev, [cf.key]: e.currentTarget.checked }))
                }
              />
            ) : cf.type === 'number' ? (
              <Input
                type="number"
                value={(custom[cf.key] as number | undefined) ?? ''}
                onChange={(e) =>
                  setCustom((prev) => ({ ...prev, [cf.key]: Number(e.currentTarget.value) || 0 }))
                }
              />
            ) : cf.type === 'date' ? (
              <Input
                type="date"
                value={(custom[cf.key] as string | undefined) ?? ''}
                onChange={(e) =>
                  setCustom((prev) => ({ ...prev, [cf.key]: e.currentTarget.value }))
                }
              />
            ) : (
              <Input
                value={
                  cf.i18n
                    ? String(
                        (custom[cf.key] as Record<string, unknown> | undefined)?.[activeLang] ?? '',
                      )
                    : String(custom[cf.key] ?? '')
                }
                onChange={(e) => {
                  if (cf.i18n) {
                    setCustom((prev) => ({
                      ...prev,
                      [cf.key]: {
                        ...(prev[cf.key] as Record<string, unknown> | undefined),
                        [activeLang]: e.currentTarget.value,
                      },
                    }));
                  } else {
                    setCustom((prev) => ({ ...prev, [cf.key]: e.currentTarget.value }));
                  }
                }}
              />
            )}
          </Field>
        ))}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 500 }}>{label}</span>
      {children}
    </label>
  );
}
